import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useLang } from "../../i18n/LanguageContext";

const LANGS = ["EN", "FR", "IT"];
const CLIENT_AREA = "https://client.asteo.eu/";

export const Navbar = () => {
  const { lang, setLang, t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const onHome = pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { id: "markets", label: t.nav.markets },
    { id: "regulation", label: t.nav.regulation },
    { id: "italy", label: t.nav.italy },
    { id: "services", label: t.nav.services },
    { id: "contact", label: t.nav.contact },
  ];

  const go = (id) => {
    setOpen(false);
    if (!onHome) {
      navigate(`/#${id}`);
      return;
    }
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  const solid = scrolled || !onHome;

  return (
    <header
      data-testid="main-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        solid ? "bg-white/85 backdrop-blur-xl border-b border-slate-200/70 shadow-[0_1px_0_rgba(15,23,42,0.04)]" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-[72px] flex items-center justify-between">
        <Link
          data-testid="nav-logo"
          to="/"
          onClick={() => onHome && window.scrollTo({ top: 0, behavior: "smooth" })}
          className="flex items-baseline gap-2 group"
        >
          <span className="font-serif text-2xl font-bold tracking-tight text-slate-900">ASTEO</span>
          <span className="hidden sm:inline text-[10px] font-mono uppercase tracking-[0.2em] text-slate-500">Luxembourg S.A.</span>
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {links.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-link-${l.id}`}
              onClick={() => go(l.id)}
              className="text-sm text-slate-600 hover:text-slate-900 transition-colors duration-300 relative after:absolute after:left-0 after:-bottom-1 after:h-px after:w-0 after:bg-emerald-700 after:transition-all after:duration-300 hover:after:w-full"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-4">
          <div data-testid="language-switcher" className="flex items-center border border-slate-200 rounded-full p-0.5 bg-white/70">
            {LANGS.map((l) => (
              <button
                key={l}
                data-testid={`lang-btn-${l.toLowerCase()}`}
                onClick={() => setLang(l)}
                className={`px-2.5 py-1 text-[11px] font-mono rounded-full transition-all duration-300 ${
                  lang === l ? "bg-slate-900 text-white" : "text-slate-500 hover:text-slate-900"
                }`}
              >
                {l}
              </button>
            ))}
          </div>
          <a
            data-testid="nav-client-area-btn"
            href={CLIENT_AREA}
            target="_blank"
            rel="noopener noreferrer"
            className="hidden md:inline-flex items-center gap-1.5 px-5 py-2.5 bg-slate-900 text-white text-sm rounded-full hover:bg-emerald-800 transition-colors duration-300"
          >
            {t.nav.portal}
            <ArrowUpRight size={14} />
          </a>
          <button data-testid="mobile-menu-toggle" onClick={() => setOpen(!open)} className="lg:hidden text-slate-900 p-1">
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-white border-b border-slate-200 overflow-hidden"
          >
            <div className="px-6 py-4 flex flex-col gap-1">
              {links.map((l) => (
                <button key={l.id} data-testid={`mobile-nav-link-${l.id}`} onClick={() => go(l.id)} className="text-left py-3 text-slate-700 border-b border-slate-100">
                  {l.label}
                </button>
              ))}
              <a
                data-testid="mobile-nav-client-area"
                href={CLIENT_AREA}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-3 inline-flex items-center justify-center gap-1.5 px-5 py-3 bg-slate-900 text-white text-sm rounded-full"
              >
                {t.nav.portal}
                <ArrowUpRight size={14} />
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
